import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../i18n";
import { Menu, X } from "lucide-react";

const Logo = () => (
  <Link to="/" data-testid="gt-logo" className="flex items-center group">
    <img
      src="/brand/logo-gt-mark.svg"
      alt="GT Strategy"
      className="h-11 md:h-12 w-auto select-none transition-opacity group-hover:opacity-90"
    />
  </Link>
);

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1 text-xs font-medium" data-testid="lang-toggle">
      <button
        data-testid="lang-pt"
        onClick={() => setLang("pt")}
        className={`px-2 py-1 transition-colors uppercase tracking-widest ${lang === "pt" ? "text-gt-cream" : "text-gt-mute hover:text-gt-ink"}`}
      >PT</button>
      <span className="text-gt-mute/40">/</span>
      <button
        data-testid="lang-en"
        onClick={() => setLang("en")}
        className={`px-2 py-1 transition-colors uppercase tracking-widest ${lang === "en" ? "text-gt-cream" : "text-gt-mute hover:text-gt-ink"}`}
      >EN</button>
    </div>
  );
};

export default function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { pathname } = useLocation();
  const onLanding = pathname === "/";
  const sectionHref = (anchor) => (onLanding ? anchor : `/${anchor}`);
  const links = [
    { h: "/stratflow", l: t.nav.method, id: "nav-method", route: true },
    { h: sectionHref("#services"), l: t.nav.services, id: "nav-services" },
    { h: sectionHref("#cases"), l: t.nav.cases, id: "nav-cases" },
    { h: sectionHref("#clients"), l: t.nav.clients, id: "nav-clients" },
    { h: sectionHref("#contact"), l: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-gt-bg/85 border-b border-gt-cream/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {links.map((it) =>
              it.route ? (
                <Link key={it.id} data-testid={it.id} to={it.h} className="gt-nav-link">
                  {it.l}
                </Link>
              ) : (
                <a key={it.id} data-testid={it.id} href={it.h} className="gt-nav-link">
                  {it.l}
                </a>
              )
            )}
          </nav>
          <div className="flex items-center gap-5">
            <LangToggle />
            <a
              href={sectionHref("#contact")}
              data-testid="header-cta"
              className="hidden md:inline-flex items-center gap-2 text-[0.7rem] px-5 py-3 btn-cream"
            >
              {t.nav.cta}
              <span aria-hidden>→</span>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-gt-cream"
              aria-label="Open menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden backdrop-blur-xl bg-gt-bg/95 border-t border-gt-cream/10" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((it) =>
              it.route ? (
                <Link
                  key={it.id}
                  to={it.h}
                  onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.18em] text-gt-ink/80 hover:text-gt-cream"
                >
                  {it.l}
                </Link>
              ) : (
                <a
                  key={it.id}
                  href={it.h}
                  onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.18em] text-gt-ink/80 hover:text-gt-cream"
                >
                  {it.l}
                </a>
              )
            )}
            <a
              href={sectionHref("#contact")}
              onClick={() => setOpen(false)}
              data-testid="mobile-cta"
              className="mt-2 inline-flex items-center gap-2 text-[0.7rem] px-5 py-3 btn-cream w-fit"
            >
              {t.nav.cta} <span>→</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
